import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AionLogoArtwork } from "@/components/aion/AionLogoArtwork";
import { bootstrapPlatformAdmin, establishBootstrapAdminSession, getBootstrapAdminStatus } from "@/services/api";

const readBootstrapToken = () => {
  const hash = new URLSearchParams(String(window.location.hash || "").replace(/^#/, ""));
  const query = new URLSearchParams(window.location.search);
  return hash.get("token") || query.get("token") || "";
};

export const BootstrapAdminScreen = ({ onAuthenticated }) => {
  const bootstrapToken = useMemo(readBootstrapToken, []);
  const [available, setAvailable] = useState(null);
  const [draft, setDraft] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initializeBootstrap = async () => {
      try {
        if (bootstrapToken) {
          await establishBootstrapAdminSession(bootstrapToken);
          if (cancelled) return;
          setSessionReady(true);
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        const result = await getBootstrapAdminStatus();
        if (cancelled) return;
        setAvailable(Boolean(result?.available));
        setSessionReady((current) => current || Boolean(result?.session_ready));
      } catch (requestError) {
        if (cancelled) return;
        setAvailable(false);
        setSessionReady(false);
        setError(requestError?.message || "Der sichere Admin-Setup-Link konnte nicht aktiviert werden.");
      }
    };

    void initializeBootstrap();
    return () => { cancelled = true; };
  }, [bootstrapToken]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!bootstrapToken && !sessionReady) {
      setError("Der sichere Admin-Setup-Link ist nicht aktiv. Bitte öffne den einmaligen Setup-Link erneut.");
      return;
    }
    if (draft.password !== draft.confirm_password) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    setPending(true);
    try {
      const result = await bootstrapPlatformAdmin({
        ...(bootstrapToken ? { bootstrap_token: bootstrapToken } : {}),
        email: draft.email.trim(),
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        password: draft.password,
      });
      await onAuthenticated(result);
      window.location.href = "/dashboard";
    } catch (requestError) {
      try {
        const status = await getBootstrapAdminStatus();
        if (status?.completed) {
          setError("Der Erst-Admin wurde erstellt. Du wirst zur Anmeldung weitergeleitet.");
          window.setTimeout(() => { window.location.href = "/login"; }, 700);
          return;
        }
      } catch {
        // Preserve the original request error if the follow-up status check fails.
      }
      setError(requestError?.message || "Erst-Admin konnte nicht erstellt werden.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="dashboard-shell" data-testid="bootstrap-admin-screen">
      <div className="dashboard-background" />
      <Card className="login-card auth-card">
        <CardHeader>
          <div className="login-logo-stack">
            <Badge variant="outline" className="w-fit">AION · FIRST ADMIN</Badge>
            <AionLogoArtwork variant="main" className="aion-auth-brand" imageClassName="aion-auth-brand-image" />
          </div>
          <CardTitle className="login-title">AION Erst-Admin einrichten</CardTitle>
          <CardDescription>
            Einmalige sichere Einrichtung des ersten PLATFORM_ADMIN. Nach Erfolg wird dieser Setup-Weg automatisch deaktiviert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {available === false ? <div className="aion-note">Der Erst-Admin-Setup ist nicht mehr verfügbar oder wurde bereits abgeschlossen.</div> : null}
          {available === true && !bootstrapToken && !sessionReady ? (
            <div className="aion-note" data-testid="bootstrap-admin-link-required">
              Der sichere Einmal-Link ist in diesem Tab nicht mehr aktiv. Öffne den Admin-Setup-Link erneut; danach bleibt die Sitzung für 30 Minuten sicher im Browser aktiv.
            </div>
          ) : null}
          {sessionReady ? <div className="aion-note" data-testid="bootstrap-admin-session-ready">Sicherer Admin-Setup ist aktiviert.</div> : null}
          {error ? <div className="error-box">{error}</div> : null}
          <form className="login-form" onSubmit={submit}>
            <div className="auth-two-column-grid">
              <label className="login-field"><span>Vorname</span><Input value={draft.first_name} onChange={(event) => setDraft((prev) => ({ ...prev, first_name: event.target.value }))} required /></label>
              <label className="login-field"><span>Nachname</span><Input value={draft.last_name} onChange={(event) => setDraft((prev) => ({ ...prev, last_name: event.target.value }))} required /></label>
            </div>
            <label className="login-field"><span>E-Mail</span><Input type="email" value={draft.email} onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))} required /></label>
            <label className="login-field"><span>Starkes Passwort</span><Input type="password" autoComplete="new-password" value={draft.password} onChange={(event) => setDraft((prev) => ({ ...prev, password: event.target.value }))} required /></label>
            <label className="login-field"><span>Passwort bestätigen</span><Input type="password" autoComplete="new-password" value={draft.confirm_password} onChange={(event) => setDraft((prev) => ({ ...prev, confirm_password: event.target.value }))} required /></label>
            <div className="aion-note">Mindestens 12 Zeichen, Groß-/Kleinbuchstaben, Zahl und Sonderzeichen.</div>
            <Button type="submit" className="login-button" disabled={pending || available !== true || (!bootstrapToken && !sessionReady)}>
              {pending ? "Admin wird erstellt …" : "Erst-Admin sicher erstellen"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
